import { useQueryParams } from 'use-query-params'
import { useState, useMemo, useCallback } from 'react'
import {
  stringQueryParamEncoder,
  makeGenericStringQueryParamEncoder,
} from 'utils/query-params/string.utils'
import { dateSelectRangeQueryParamEncoder } from 'utils/query-params/dateSelectRange.utils'
import { type RetailerLocationSelectValueType } from 'legacy/dash-components/retailer-location-select/utils/options.types'
import { type DateSelectRangeValueType } from 'legacy/dash-components/date-select-range/utils/dateSelectRange.types'
import { type OrderSource, type OrderStatus } from 'legacy/sections/orders/utils/orders.types'
import { retailerLocationSelectQueryParamEncoder } from 'utils/query-params/retailerLocationSelect.utils'

const useOrdersListQueryParamsState = () =>
  useQueryParams({
    orderNumber: stringQueryParamEncoder,
    orderStatus: makeGenericStringQueryParamEncoder<OrderStatus>(),
    orderSource: makeGenericStringQueryParamEncoder<OrderSource>(),
    dateSelectRangeValue: dateSelectRangeQueryParamEncoder,
    retailerLocationSelectValue: retailerLocationSelectQueryParamEncoder,
  })

export const useOrdersListQueryParamsBehaviour = () => {
  const [queryValue, setQueryValue] = useOrdersListQueryParamsState()

  const setDateSelectRangeValue = useCallback(
    (nextDateSelectRangeValue: DateSelectRangeValueType) => {
      setQueryValue({
        ...queryValue,
        dateSelectRangeValue: nextDateSelectRangeValue,
      })
    },
    [queryValue, setQueryValue]
  )

  const setRetailerLocationSelectValue = useCallback(
    (nextRetailerLocationSelectValue: RetailerLocationSelectValueType) => {
      setQueryValue({
        ...queryValue,
        retailerLocationSelectValue: nextRetailerLocationSelectValue,
      })
    },
    [queryValue, setQueryValue]
  )

  const setOrderSource = useCallback(
    (nextOrderSource: OrderSource) => {
      setQueryValue({
        ...queryValue,
        orderSource: nextOrderSource,
      })
    },
    [queryValue, setQueryValue]
  )

  const setOrderNumber = useCallback(
    (nextOrderNumber: string) => {
      if (nextOrderNumber.length === 0) {
        setQueryValue({
          ...queryValue,
          orderNumber: undefined,
        })
      } else {
        setQueryValue({
          ...queryValue,
          orderNumber: nextOrderNumber,
        })
      }
    },
    [queryValue, setQueryValue]
  )

  const setOrderStatus = useCallback(
    (nextOrderStatus: OrderStatus) => {
      setQueryValue({
        ...queryValue,
        orderStatus: nextOrderStatus,
      })
    },
    [queryValue, setQueryValue]
  )

  const {
    dateSelectRangeValue,
    orderSource,
    orderNumber,
    retailerLocationSelectValue,
    orderStatus,
  } = queryValue

  const [pendingOrderNumber, setPendingOrderNumber] = useState<string>(orderNumber || '')

  const clearSearch = useCallback(() => {
    setPendingOrderNumber('')
    setOrderNumber('')
  }, [setOrderNumber])

  const executeSearch = useCallback(
    () => setOrderNumber(pendingOrderNumber),
    [pendingOrderNumber, setOrderNumber]
  )
  const clearSearchDisabled = useMemo(
    () => pendingOrderNumber.length < 1,
    [pendingOrderNumber.length]
  )

  const executeSearchDisabled = clearSearchDisabled

  return {
    dateSelectRangeValue,
    setDateSelectRangeValue,
    orderStatus,
    setOrderStatus,
    orderSource,
    setOrderSource,
    // Note that you can't set the order number directly, you need to set it
    // by setting the pending order number, then executing a search
    orderNumber,
    pendingOrderNumber,
    setPendingOrderNumber,
    clearSearch,
    executeSearch,
    clearSearchDisabled,
    executeSearchDisabled,
    retailerLocationSelectValue,
    setRetailerLocationSelectValue,
  }
}

export type OrdersListQueryParamsBehaviour = ReturnType<typeof useOrdersListQueryParamsBehaviour>
